import React from 'react';
import { Link } from 'react-router-dom';
import { useGamificationContext } from '../context/GamificationContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import Button from '../components/ui/Button';
import * as FiIcons from 'react-icons/fi';
import { FiLock, FiAward, FiStar, FiTrendingUp } from 'react-icons/fi';

const Achievements = () => {
  const { userStats, unlockedAchievements, gamificationEnabled } = useGamificationContext();

  const getIcon = (iconName) => {
    const Icon = FiIcons[iconName] || FiAward;
    return <Icon className="w-6 h-6" />;
  };

  if (!gamificationEnabled) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto flex flex-col items-center justify-center min-h-[50vh] text-center">
        <FiAward className="w-16 h-16 text-slate-300 dark:text-slate-600 mb-4" />
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Gamification is Disabled</h2>
        <p className="text-slate-600 dark:text-slate-400 mb-6 max-w-md">
          Enable gamification in your settings to view achievements, track your level, and make training more rewarding.
        </p>
        <Link to="/settings">
          <Button variant="primary">Go to Settings</Button>
        </Link>
      </div>
    );
  }

  const unlockedCount = unlockedAchievements.filter(a => a.unlocked).length;
  const totalCount = unlockedAchievements.length;
  const completionPercentage = Math.round((unlockedCount / totalCount) * 100);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-2">My Journey</h1>
        <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400">
          Track your rewards, level progression, and badges.
        </p>
      </div>

      {/* Level Card */}
      <Card className="mb-8 border-blue-200 dark:border-blue-900/50 bg-gradient-to-br from-blue-50 to-white dark:from-slate-800 dark:to-slate-900 overflow-hidden relative">
        <div className="absolute top-0 right-0 p-4 opacity-10 dark:opacity-5">
          <FiStar className="w-48 h-48 text-blue-600 dark:text-blue-400" />
        </div>
        <CardContent className="p-6 sm:p-8 relative z-10">
          <div className="flex flex-col sm:flex-row items-center gap-6">
            <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-blue-600 dark:bg-blue-500 flex items-center justify-center shadow-lg border-4 border-white dark:border-slate-800">
              <div className="text-center text-white">
                <p className="text-xs font-medium uppercase tracking-wider opacity-80">Level</p>
                <p className="text-4xl sm:text-5xl font-bold">{userStats.level}</p>
              </div>
            </div>
            <div className="flex-1 w-full text-center sm:text-left">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-1">Rank: White Belt</h2>
              <p className="text-slate-600 dark:text-slate-400 mb-4">Total XP: {userStats.xp.toLocaleString()}</p>
              
              <div className="space-y-2">
                <div className="flex justify-between text-sm font-medium">
                  <span className="text-blue-700 dark:text-blue-400">Progress to Level {userStats.level + 1}</span>
                  <span className="text-blue-700 dark:text-blue-400">{Math.round(userStats.progressToNext)}%</span>
                </div>
                <div className="w-full bg-blue-100 dark:bg-slate-700 rounded-full h-4 overflow-hidden shadow-inner">
                  <div 
                    className="bg-blue-600 dark:bg-blue-500 h-full rounded-full transition-all duration-500 ease-out relative overflow-hidden"
                    style={{ width: `${userStats.progressToNext}%` }}
                  >
                    <div className="absolute inset-0 bg-white opacity-20 animate-pulse"></div>
                  </div>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 text-right">
                  {Math.round(userStats.nextLevelXp - userStats.xp)} XP to next level
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Badges Grid */}
      <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
        <FiAward className="w-5 h-5 mr-2 text-yellow-600 dark:text-yellow-500" />
        Achievements ({unlockedCount}/{totalCount})
      </h2>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {unlockedAchievements.map((achievement) => (
          <Card 
            key={achievement.id} 
            className={`transition-all duration-300 ${
              achievement.unlocked 
                ? 'border-yellow-200 bg-yellow-50/50 dark:border-yellow-900/30 dark:bg-yellow-900/10' 
                : 'border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/50 opacity-70 grayscale'
            }`}
          >
            <CardContent className="p-5 flex items-start space-x-4">
              <div className={`p-3 rounded-full flex-shrink-0 ${
                achievement.unlocked 
                  ? 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/40 dark:text-yellow-500' 
                  : 'bg-slate-200 text-slate-400 dark:bg-slate-700 dark:text-slate-500'
              }`}>
                {achievement.unlocked ? getIcon(achievement.icon) : <FiLock className="w-6 h-6" />}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className={`font-bold text-base ${achievement.unlocked ? 'text-slate-900 dark:text-slate-100' : 'text-slate-500 dark:text-slate-400'}`}>
                  {achievement.title}
                </h3>
                <p className={`text-sm mt-1 leading-relaxed ${achievement.unlocked ? 'text-slate-600 dark:text-slate-400' : 'text-slate-500 dark:text-slate-500'}`}>
                  {achievement.description}
                </p>
                {achievement.unlocked && (
                  <span className="inline-block mt-2 text-xs font-medium text-yellow-700 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/40 px-2 py-0.5 rounded-full">
                    Unlocked
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default Achievements;